import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { PageHead } from "@/components/PageHead";
import { UserSignUpFormContent } from "./components/UserSignUpFormContent";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { StepProgress } from "@/components/StepProgress";
import { MarketingPanel } from "./components/MarketingPanel";
import {
  STEP1_PANEL_CONTENT,
  ADVISOR_STEP1_PANEL_CONTENT,
  PROGRAM_JOIN_STEP1_PANEL_CONTENT,
} from "@/constants/signupPanelContent";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getSignupStepLabels } from "@/constants/signupSteps";
import { signupStepViewed } from "@/utils/tracking";
import { initSessionReplay } from "@/utils/amplitude";
import { signupRoleFromParam } from "@/utils/roleUtils";

export const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const roleParam = searchParams.get("role");
  const code = searchParams.get("code");
  const joinProgramId = searchParams.get("join") || undefined;
  const prefillEmail = searchParams.get("email") || undefined;
  // `?role=expert` → the advisor account role; no param → achiever signup.
  const role = signupRoleFromParam(roleParam);
  const isAlreadyExists = searchParams.get("error") === "user_already_exists";
  // Set on achiever invite-by-email links (see invite_achievers_by_email) so an
  // invited achiever goes through the full 3-step signup instead of the 2-step
  // flow the public "/join/:slug" page sends organic joiners through.
  const isInvitedJoin = searchParams.get("invited") === "1";

  // Persist join program id + role across the signup flow (step 1 → email → step 2).
  // Only SET — never clear here. If SignUpPage renders without join (e.g. OAuth error
  // redirect) we must not wipe a value that was set moments earlier in the same flow.
  // Cleanup happens on logout instead. `program_join_invited` is the one exception:
  // it's fully determined by *this* page load's `invited` param, so it's reset here
  // every time a `join` param is present to avoid a stale flag leaking into an
  // unrelated organic join later in the same browser.
  useEffect(() => {
    if (joinProgramId && !isAlreadyExists) {
      localStorage.setItem("program_join_id", joinProgramId);
      localStorage.setItem("program_join_role", role);
      if (isInvitedJoin) {
        localStorage.setItem("program_join_invited", "1");
      } else {
        localStorage.removeItem("program_join_invited");
      }
    }
  }, [joinProgramId, role, isAlreadyExists, isInvitedJoin]);

  useEffect(() => {
    if (!isAlreadyExists) {
      initSessionReplay();
      signupStepViewed({ step: 1, role });
    }
  }, [role, isAlreadyExists]);

  // When the backend detects an already-existing account it redirects here with
  // ?error=user_already_exists (plus email/join/role params). Immediately send
  // the user to /login — no signup page renders at all.
  if (isAlreadyExists) {
    return (
      <Navigate
        to="/login"
        state={{
          email: prefillEmail,
          role,
          joinProgramId,
          toastMessage: "An account with this email already exists. Please sign in.",
        }}
        replace
      />
    );
  }

  // Achievers walk 3 steps; Experts 4 (Operator DNA, then S.T.A.R. Portfolio).
  // The join paths no longer collapse to 2.
  const stepLabels = getSignupStepLabels(role === "advisor");

  const panelContent =
    role === "advisor"
      ? ADVISOR_STEP1_PANEL_CONTENT
      : joinProgramId
        ? PROGRAM_JOIN_STEP1_PANEL_CONTENT
        : STEP1_PANEL_CONTENT;

  return (
    <>
      <PageHead
        title="Sign Up"
        description="Create your AchieveOS account"
        additionalProps={{
          signup_role: role,
          signup_code: code,
          utm_source: searchParams.get("utm_source"),
          utm_medium: searchParams.get("utm_medium"),
          utm_campaign: searchParams.get("utm_campaign"),
        }}
      />

      <EnvironmentBadge />

      {/* Fixed to the viewport — independent of #root's height chain (Radix's
          .radix-themes wrapper has no height:100%, so h-full alone would
          collapse to content height and let #root scroll the whole page,
          panel included). This guarantees only the left column scrolls. */}
      <div className="fixed inset-0 flex flex-col overflow-hidden font-inter bg-signup-form-bg bg-surface-page-bg">
        {/* Body row — fills full height, panels scroll independently */}
        <div className="flex flex-1 min-h-0">
          {/* Left/Center: light gradient bg, form centered within */}
          <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
            <div className="flex flex-col items-center px-4 md:px-8 min-h-full py-8">
              {/* Constrained width — centered */}
              <div className="w-full max-w-[620px] flex flex-col">
                {/* Logo + Theme Toggle */}
                <div className="flex items-center justify-between mb-10">
                  <img
                    src={fsvLogo}
                    alt="AchieveOS"
                    className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <img
                    src={fsvLogoDark}
                    alt="AchieveOS"
                    className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <ThemeToggle />
                </div>

                {/* Step progress — matches the role-specific 3rd-step label used
                    on steps 2 and 3 (SignUpStepTwo.tsx / SignUpStepThree.tsx) so
                    it doesn't relabel mid-flow. */}
                <StepProgress
                  currentStep={1}
                  totalSteps={stepLabels.length}
                  stepLabels={stepLabels}
                />

                {/* Form card */}
                <div className="bg-theme-bg-surface border border-theme-border rounded-2xl px-4 md:px-10 pt-10 pb-10 mt-8">
                  <UserSignUpFormContent
                    role={role}
                    defaultCode={code || undefined}
                    joinProgramId={joinProgramId}
                  />
                </div>

                {/* Sign in */}
                <p className="text-center text-14 text-theme-text-muted py-6">
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate("/login")}
                    className="text-primary-purple font-semibold hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>

          {/* Right: purple marketing panel */}
          <div className="hidden md:block">
            <MarketingPanel content={panelContent} />
          </div>
        </div>
      </div>
    </>
  );
};
